import { useMemo, useState } from 'react';
import Link from 'next/link';

import ChevronRight from '@/icons/light/chevron-right.svg';
import { Container, Text } from '@/atoms';

import { isBotonContacto, useBotonContacto } from '@/hooks/useBotonContacto';
import {
  blockSpacing,
  eyebrowClass,
  formatNumberToCurrency,
  getRealUrl,
  sectionBgProps,
  textStyleClass,
} from '@/utils';

const RANGE_CLASS =
  'h-4 w-full cursor-pointer appearance-none rounded-full bg-transparent ' +
  '[&::-webkit-slider-runnable-track]:h-4 [&::-webkit-slider-runnable-track]:rounded-full ' +
  '[&::-moz-range-track]:h-4 [&::-moz-range-track]:rounded-full ' +
  '[&::-webkit-slider-thumb]:-mt-1 [&::-webkit-slider-thumb]:size-6 [&::-webkit-slider-thumb]:appearance-none ' +
  '[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-highlight [&::-webkit-slider-thumb]:bg-neutrals-0 ' +
  '[&::-moz-range-thumb]:size-6 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-highlight [&::-moz-range-thumb]:bg-neutrals-0';

// Solo hay dos tipos de hipoteca; el tipo fijo arranca con esta tasa estimada
// (el usuario la ajusta luego con el stepper). Confirmar valor con negocio.
const INTERES_FIJO = 2.9;

// Lista de localizaciones con su tipo impositivo aproximado para el cálculo
// de impuestos de compra. Hardcoded por ahora (cambios suelen ser raros y
// están atadas a normativa autonómica). Si se quisiera tenant-driven, mover
// a una collection en Strapi en una siguiente iteración.
//   - `nuevo` aplica IVA + AJD (vivienda nueva)
//   - `segundo` aplica ITP (vivienda de segunda mano)
const LOCALIZACIONES = [
  'Madrid',
  'Barcelona',
  'Valencia',
  'Sevilla',
  'Bilbao',
  'Otras',
] as const;
type Localizacion = (typeof LOCALIZACIONES)[number];
type EstadoInmueble = 'nuevo' | 'segundo';
const TAX_TABLE: Record<Localizacion, Record<EstadoInmueble, number>> = {
  Madrid: { nuevo: 10.75, segundo: 6 },
  Barcelona: { nuevo: 11.5, segundo: 10 },
  Valencia: { nuevo: 11.5, segundo: 10 },
  Sevilla: { nuevo: 11.5, segundo: 7 },
  Bilbao: { nuevo: 10.5, segundo: 4 },
  Otras: { nuevo: 10.5, segundo: 7 },
};

const FIELD_LABEL = 'text-[18px] font-bold text-[var(--inputbg)]';
const FIELD_BOX =
  'border-input-border flex h-12 items-center justify-center rounded-xl border bg-neutrals-0 px-4 text-[18px] text-[var(--inputbg)]';

// El track combina el tramo "relleno" (amarillo) hasta el valor y el resto en
// el color de borde; se calcula con un gradiente según el porcentaje recorrido.
const trackStyle = (pct: number) => ({
  background: `linear-gradient(to right, rgb(var(--tw-color-highlight)) 0% ${pct}%, var(--inputborder) ${pct}% 100%)`,
});

export const ComponentRevisadasCalculadoraHipoteca = ({
  Eyebrow,
  Titulo,
  Subtitulo,
  Descripcion,
  PrecioInmuebleDefecto,
  PrecioMin,
  PrecioMax,
  PrecioStep,
  AhorroPorcentajeDefecto,
  AhorroPorcentajeMin,
  AhorroPorcentajeMax,
  PlazoDefecto,
  PlazoMin,
  PlazoMax,
  InteresDefecto,
  InteresStep,
  ImpuestosPorcentaje,
  TextoResultado,
  TextoImpuestos,
  TextoPrestamo,
  TextoCuota,
  Disclaimer,
  CtaBoton,
  EyebrowEstilo,
  TituloEstilo,
  SubtituloEstilo,
  DescripcionEstilo,
  Forma,
  precioInmueble,
  FondoSeccion,
  Espaciado,
  // Cuando `MostrarResumen=true`, el bloque pinta un panel destacado a la
  // derecha con el desglose (préstamo, intereses, TAE, total, aval) + CTA,
  // y el header pasa por encima del row. Default `false` → comportamiento
  // histórico intacto (header-left + form-right en una sola fila).
  MostrarResumen,
  TaeSpreadPorcentaje,
  CtaResumen,
  FondoResumen,
  Ancla,
  noContainer = false,
}: any) => {
  const formaSvgUrl = Forma?.Svg?.url ? getRealUrl(Forma.Svg.url) : undefined;
  const [precio, setPrecio] = useState<number>(
    precioInmueble ?? PrecioInmuebleDefecto,
  );
  const [ahorroPct, setAhorroPct] = useState<number>(AhorroPorcentajeDefecto);
  const [plazo, setPlazo] = useState<number>(PlazoDefecto);
  const [interes, setInteres] = useState<number>(InteresDefecto);
  const { modal, abrirContacto } = useBotonContacto();
  const [tipoInteres, setTipoInteres] = useState<'fijo' | 'variable'>(
    'variable',
  );
  // Inputs nuevos del modo MostrarResumen. En modo legacy se ignoran (el cálculo
  // de impuestos usa `ImpuestosPorcentaje` static y no se renderiza ningún campo).
  const [localizacion, setLocalizacion] = useState<Localizacion>('Madrid');
  const [estado, setEstado] = useState<EstadoInmueble>('segundo');

  const cambiarTipo = (tipo: 'fijo' | 'variable') => {
    setTipoInteres(tipo);
    setInteres(tipo === 'fijo' ? INTERES_FIJO : InteresDefecto);
  };

  const {
    ahorro,
    impuestos,
    prestamo,
    cuota,
    tae,
    interesesTotal,
    totalConIntereses,
    costeTotalInmueble,
    costeTotalConHipoteca,
  } = useMemo(() => {
    const ahorroAportado = Math.round((precio * ahorroPct) / 100);
    // En modo resumen, el porcentaje de impuestos viene de la tabla
    // (Localización × Estado). En legacy se usa el `ImpuestosPorcentaje`
    // del bloque como antes.
    const impuestosPct = MostrarResumen
      ? (TAX_TABLE[localizacion]?.[estado] ?? ImpuestosPorcentaje)
      : ImpuestosPorcentaje;
    const impuestosGastos = Math.round((precio * impuestosPct) / 100);
    const principal = Math.max(precio - ahorroAportado, 0);

    const r = interes / 100 / 12;
    const n = plazo * 12;

    let monthly = 0;
    if (n > 0) {
      monthly =
        r === 0
          ? principal / n
          : (principal * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    }

    // TAE = ((1 + r/12)^12 - 1) * 100 + spread opcional (gastos del banco que
    // los tenants pueden afinar via `TaeSpreadPorcentaje`; default 0).
    const spread =
      typeof TaeSpreadPorcentaje === 'number' ? TaeSpreadPorcentaje : 0;
    const taeNominal = (Math.pow(1 + interes / 100 / 12, 12) - 1) * 100;
    const tae = Number.isFinite(taeNominal) ? taeNominal + spread : 0;
    const interesesTotal = Math.max(monthly * n - principal, 0);
    const totalConIntereses = principal + interesesTotal;
    // Coste total del inmueble = precio + impuestos compra.
    const costeTotalInmueble = precio + impuestosGastos;
    // Coste total con hipoteca = todo lo que el comprador acaba desembolsando:
    // entrada (ahorro) + lo prestado + intereses + impuestos compra.
    const costeTotalConHipoteca =
      ahorroAportado + principal + interesesTotal + impuestosGastos;

    return {
      ahorro: ahorroAportado,
      impuestos: impuestosGastos,
      prestamo: principal,
      cuota: Number.isFinite(monthly) ? monthly : 0,
      tae,
      interesesTotal,
      totalConIntereses,
      costeTotalInmueble,
      costeTotalConHipoteca,
    };
  }, [
    precio,
    ahorroPct,
    plazo,
    interes,
    ImpuestosPorcentaje,
    TaeSpreadPorcentaje,
    MostrarResumen,
    localizacion,
    estado,
  ]);

  const precioPct = ((precio - PrecioMin) / (PrecioMax - PrecioMin)) * 100;
  const ahorroSliderPct =
    ((ahorroPct - AhorroPorcentajeMin) /
      (AhorroPorcentajeMax - AhorroPorcentajeMin)) *
    100;
  const plazoPct = ((plazo - PlazoMin) / (PlazoMax - PlazoMin)) * 100;

  // Sólo se renderiza si hay contenido de cabecera. Si no, `null` evita un div
  // vacío que, por el `gap-12`/`flex-1` del contenedor, metía 48px muertos
  // (o media columna en modo legacy) encima del formulario (p.ej. en el
  // simulador, que no usa título).
  const header =
    Eyebrow || Titulo || Subtitulo || Descripcion ? (
      <div className="flex flex-1 flex-col gap-8">
        <div className="flex flex-col gap-2">
          {Eyebrow && (
            <Text
              className={`text-headline text-primary-500 ${
                eyebrowClass(EyebrowEstilo) ?? ''
              }`}
            >
              {Eyebrow}
            </Text>
          )}
          {Titulo && (
            <Text
              as="h2"
              className={`text-headline text-headline-h2 ${
                textStyleClass(TituloEstilo) ?? ''
              }`}
            >
              <span dangerouslySetInnerHTML={{ __html: Titulo }} />
            </Text>
          )}
          {Subtitulo && (
            <Text
              className={`text-headline text-headline-h4 ${
                textStyleClass(SubtituloEstilo) ?? ''
              }`}
            >
              {Subtitulo}
            </Text>
          )}
        </div>
        {Descripcion && (
          <Text
            className={`text-body text-body-m ${
              textStyleClass(DescripcionEstilo) ?? ''
            }`}
          >
            {Descripcion}
          </Text>
        )}
      </div>
    ) : null;

  const form = (
    <div className="relative flex flex-1 flex-col">
      {formaSvgUrl && (
        // El mask SVG (Forma orgánica) sólo aplica desde md+: en móvil la
        // forma queda demasiado estrecha/distorsionada y aporta poco, así
        // que renderizamos el contenedor como un card rectangular limpio.
        <span
          aria-hidden
          className="bg-neutrals-0 absolute inset-0 hidden md:block"
          style={{
            WebkitMaskImage: `url(${formaSvgUrl})`,
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            WebkitMaskSize: '100% 100%',
            maskImage: `url(${formaSvgUrl})`,
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
            maskSize: '100% 100%',
            filter: 'drop-shadow(0 14px 24px rgba(0, 0, 0, 0.1))',
          }}
        />
      )}
      <div
        className={`relative flex flex-1 flex-col gap-6 ${
          formaSvgUrl
            ? // En móvil el card es rectángulo blanco con borde + padding
              // (idéntico al branch "sin Forma"); en md+ se desnuda para
              // que el mask de la Forma sea la decoración.
              `${MostrarResumen ? 'rounded-card' : 'rounded-custom'} border-input-border bg-neutrals-0 border p-6 md:rounded-none md:border-0 md:bg-transparent md:p-20`
            : `${MostrarResumen ? 'rounded-card' : 'rounded-custom'} border-input-border bg-neutrals-0 border p-6 md:p-10`
        }`}
      >
        <div className="flex flex-col gap-2.5">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <span className={FIELD_LABEL}>Precio del inmueble</span>
            <span className={`${FIELD_BOX} w-full md:w-[200px]`}>
              {formatNumberToCurrency(precio)}
            </span>
          </div>
          <input
            type="range"
            className={RANGE_CLASS}
            style={trackStyle(precioPct)}
            min={PrecioMin}
            max={PrecioMax}
            step={PrecioStep}
            value={precio}
            onChange={(e) => setPrecio(Number(e.target.value))}
            aria-label="Precio del inmueble"
          />
        </div>

        <div className="flex flex-col gap-2.5">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <span className={FIELD_LABEL}>Ahorro aportado</span>
            <span className={`${FIELD_BOX} w-full gap-2 md:w-[200px]`}>
              <span>{formatNumberToCurrency(ahorro)}</span>
              <span className="text-input-placeholder text-[var(--inputplaceholder,#839ea8)]">
                {ahorroPct}%
              </span>
            </span>
          </div>
          <input
            type="range"
            className={RANGE_CLASS}
            style={trackStyle(ahorroSliderPct)}
            min={AhorroPorcentajeMin}
            max={AhorroPorcentajeMax}
            step={1}
            value={ahorroPct}
            onChange={(e) => setAhorroPct(Number(e.target.value))}
            aria-label="Ahorro aportado"
          />
          {MostrarResumen && (
            <span className="text-[14px] leading-5 text-[var(--inputbg)]/70">
              Recuerda que los bancos suelen pedir una aportación mínima del 20%
              más gastos
            </span>
          )}
        </div>

        <div className="flex flex-col gap-2.5">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <span className={FIELD_LABEL}>Plazo en años</span>
            <span className={`${FIELD_BOX} w-full md:w-[200px]`}>{plazo}</span>
          </div>
          <input
            type="range"
            className={RANGE_CLASS}
            style={trackStyle(plazoPct)}
            min={PlazoMin}
            max={PlazoMax}
            step={1}
            value={plazo}
            onChange={(e) => setPlazo(Number(e.target.value))}
            aria-label="Plazo en años"
          />
        </div>

        <div className="flex flex-col gap-2.5">
          <span className={FIELD_LABEL}>Tipo de interés</span>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => cambiarTipo('fijo')}
                className={`text-[18px] font-bold ${
                  tipoInteres === 'fijo'
                    ? 'text-[var(--inputbg)]'
                    : 'text-text-neutrals-500'
                }`}
              >
                Fijo
              </button>
              <button
                type="button"
                role="switch"
                aria-checked={tipoInteres === 'variable'}
                aria-label="Tipo de interés fijo o variable"
                onClick={() =>
                  cambiarTipo(tipoInteres === 'fijo' ? 'variable' : 'fijo')
                }
                className="bg-highlight relative h-[23px] w-10 shrink-0 rounded-full p-[3px]"
              >
                <span
                  className={`bg-neutrals-0 block size-[17px] rounded-full shadow transition-transform ${
                    tipoInteres === 'variable' ? 'translate-x-[17px]' : ''
                  }`}
                />
              </button>
              <button
                type="button"
                onClick={() => cambiarTipo('variable')}
                className={`text-[18px] font-bold ${
                  tipoInteres === 'variable'
                    ? 'text-[var(--inputbg)]'
                    : 'text-text-neutrals-500'
                }`}
              >
                Variable
              </button>
            </div>
            {MostrarResumen ? (
              // En modo resumen, sólo se muestra el valor — el tipo se cambia
              // con el toggle Fijo/Variable (cada uno trae su rate por defecto).
              <span className={`${FIELD_BOX} w-full md:w-[200px]`}>
                {interes.toLocaleString('es-ES')}%
              </span>
            ) : (
              <div
                className={`${FIELD_BOX} w-full justify-between md:w-[200px]`}
              >
                <button
                  type="button"
                  className="px-1 text-xl leading-none"
                  onClick={() =>
                    setInteres((v) =>
                      Math.max(0, Number((v - InteresStep).toFixed(2))),
                    )
                  }
                  aria-label="Reducir tipo de interés"
                >
                  -
                </button>
                <span>{interes.toLocaleString('es-ES')}%</span>
                <button
                  type="button"
                  className="px-1 text-xl leading-none"
                  onClick={() =>
                    setInteres((v) => Number((v + InteresStep).toFixed(2)))
                  }
                  aria-label="Aumentar tipo de interés"
                >
                  +
                </button>
              </div>
            )}
          </div>
        </div>

        {MostrarResumen && (
          <>
            <div className="flex flex-col gap-2.5">
              <span className={FIELD_LABEL}>Localización del inmueble</span>
              <select
                value={localizacion}
                onChange={(e) =>
                  setLocalizacion(e.target.value as Localizacion)
                }
                className={`${FIELD_BOX} w-full md:w-[300px]`}
                aria-label="Localización del inmueble"
              >
                {LOCALIZACIONES.map((loc) => (
                  <option key={loc} value={loc}>
                    {loc}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2.5">
              <span className={FIELD_LABEL}>Estado del inmueble</span>
              <div className="flex flex-wrap gap-6">
                {(
                  [
                    { value: 'nuevo' as const, label: 'Nuevo' },
                    { value: 'segundo' as const, label: 'Segunda mano' },
                  ] as const
                ).map((opt) => (
                  <label
                    key={opt.value}
                    className="text-text-primary flex cursor-pointer items-center gap-2 text-[18px]"
                  >
                    <input
                      type="radio"
                      name="estado-inmueble"
                      value={opt.value}
                      checked={estado === opt.value}
                      onChange={() => setEstado(opt.value)}
                      className="accent-highlight size-4"
                    />
                    <span
                      className={
                        estado === opt.value
                          ? 'text-[var(--inputbg)]'
                          : 'text-text-neutrals-500'
                      }
                    >
                      {opt.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </>
        )}

        {/* En modo resumen, las líneas de resultado, CTA y disclaimer viven
              fuera del form (en el panel derecho). En legacy se mantienen aquí. */}
        {!MostrarResumen && (
          <div className="border-border-strong flex flex-col gap-2 border-t border-[var(--inputborder)] pt-4 text-[var(--inputbg)]">
            <div className="flex justify-between text-[18px]">
              <span>{TextoImpuestos ?? 'Impuestos y gastos:'}</span>
              <span>{formatNumberToCurrency(impuestos)}</span>
            </div>
            <div className="flex justify-between text-[18px]">
              <span>{TextoPrestamo ?? 'Importe del préstamo:'}</span>
              <span>{formatNumberToCurrency(prestamo)}</span>
            </div>
            <div className="flex items-center justify-between font-bold">
              <span className="text-[18px]">
                {TextoCuota ?? 'Tu cuota mensual:'}
              </span>
              <span className="text-[22px]">
                {formatNumberToCurrency(cuota)}
              </span>
            </div>
          </div>
        )}

        {!MostrarResumen && CtaBoton?.Texto && isBotonContacto(CtaBoton) && (
          <button
            type="button"
            onClick={() => abrirContacto(CtaBoton)}
            className="bg-ctacolor text-neutrals-0 flex w-fit min-w-[180px] items-center gap-2 rounded-2xl bg-[var(--ctacolor)] px-6 py-3 text-[18px] transition-colors hover:bg-[rgb(var(--tw-color-button-bg-hover,var(--tw-color-secondary-500)))]"
          >
            {CtaBoton.Texto}
            <ChevronRight className="size-4 shrink-0" />
          </button>
        )}
        {!MostrarResumen &&
          CtaBoton?.Texto &&
          CtaBoton?.Link &&
          !isBotonContacto(CtaBoton) && (
            <Link
              href={CtaBoton.Link}
              prefetch={false}
              className="bg-ctacolor text-neutrals-0 flex w-fit min-w-[180px] items-center gap-2 rounded-2xl bg-[var(--ctacolor)] px-6 py-3 text-[18px] transition-colors hover:bg-[rgb(var(--tw-color-button-bg-hover,var(--tw-color-secondary-500)))]"
            >
              {CtaBoton.Texto}
              <ChevronRight className="size-4 shrink-0" />
            </Link>
          )}

        {!MostrarResumen && (Disclaimer?.Texto || Disclaimer?.Link?.Titulo) && (
          // Figma 2182:5941 → 18px/28px Roboto regular (no `text-body-m`
          // porque HoM lo tiene en 20px y rompe el wrap del disclaimer).
          <Text className="text-body text-[18px] leading-[28px] text-[var(--inputbg)]">
            {Disclaimer?.Texto}
            {Disclaimer?.Texto && Disclaimer?.Link?.Titulo ? ' ' : ''}
            {Disclaimer?.Link?.Titulo && Disclaimer?.Link?.Link && (
              // El selector global `a { @apply text-link text-primary-500 }`
              // de globals.css fuerza 13px + teal en todos los `<a>`;
              // re-apuntamos a la tipografía del párrafo para que la
              // línea no se rompa y el color quede como Figma.
              <Link
                href={Disclaimer.Link.Link}
                prefetch={false}
                className="text-[18px] leading-[28px] text-[var(--inputbg)] underline"
              >
                {Disclaimer.Link.Titulo}
              </Link>
            )}
          </Text>
        )}
      </div>
    </div>
  );

  // Filas del desglose blanco — label tenue a la izquierda, valor bold a la derecha.
  // La fila resaltada usa un tinte del color de marca de fondo + texto en color
  // de marca (Figma 2182:6632 → bg rgba(maincolor,0.1) / texto maincolor).
  // `primary-500` está cableado a `--maincolor`, así que el tinte sale del mismo color.
  const breakdownRow = (
    label: string,
    value: string,
    opts?: { highlight?: boolean },
  ) => (
    <div
      className={`flex items-center justify-between px-4 py-2 text-[16px] leading-6 ${
        opts?.highlight
          ? 'rounded-custom bg-[rgb(var(--tw-color-primary-500)/0.1)]'
          : ''
      }`}
    >
      <span
        className={opts?.highlight ? 'text-primary-500' : 'text-text-primary'}
      >
        {label}
      </span>
      <span
        className={`font-bold ${
          opts?.highlight ? 'text-primary-500' : 'text-text-primary'
        }`}
      >
        {value}
      </span>
    </div>
  );

  // Panel superior (color de marca) — cuota grande + 2 líneas de desglose rápido.
  // Fondo = maincolor del themeconfig (Figma 2182:6601 → --background/brand).
  // `FondoResumen` (Strapi) sigue permitiendo un override puntual por tenant.
  const resumenTopPanel = (
    <div
      className="text-neutrals-0 rounded-card flex flex-col gap-6 p-8 lg:p-10"
      style={{
        backgroundColor: FondoResumen || 'var(--maincolor)',
      }}
    >
      <div className="flex flex-col gap-1">
        <span className="text-[18px] leading-7 font-bold">
          Tu cuota mensual
        </span>
        <span className="text-[56px] leading-[1.05] font-bold lg:text-[72px]">
          {formatNumberToCurrency(cuota)}
        </span>
      </div>

      <div className="border-neutrals-0/20 flex flex-col gap-2 border-t pt-4">
        <div className="flex items-center justify-between text-[16px]">
          <span>Impuestos y gastos:</span>
          <span>{formatNumberToCurrency(impuestos)}</span>
        </div>
        <div className="flex items-center justify-between text-[16px]">
          <span>Importe del préstamo:</span>
          <span>{formatNumberToCurrency(prestamo)}</span>
        </div>
      </div>
    </div>
  );

  // Panel inferior (blanco) — desglose de coste del inmueble + coste hipoteca,
  // separados por una línea sutil. Última fila ("Coste total con hipoteca")
  // resaltada con un tinte del color de marca y esquinas redondeadas.
  const resumenBottomPanel = (
    <div className="border-input-border bg-neutrals-0 rounded-card flex flex-col gap-3 border p-4 lg:p-6">
      <div className="flex flex-col gap-1">
        {breakdownRow('Precio del inmueble', formatNumberToCurrency(precio))}
        {breakdownRow(
          'Impuestos y gastos de la compra',
          formatNumberToCurrency(impuestos),
        )}
        {breakdownRow(
          'Coste total del inmueble',
          formatNumberToCurrency(costeTotalInmueble),
        )}
      </div>
      <div className="border-input-border flex flex-col gap-1 border-t pt-3">
        {breakdownRow('Ahorro aportado', formatNumberToCurrency(ahorro))}
        {breakdownRow('Importe hipoteca', formatNumberToCurrency(prestamo))}
        {breakdownRow(
          'Intereses hipoteca',
          formatNumberToCurrency(interesesTotal),
        )}
        {breakdownRow(
          'Coste total con hipoteca',
          formatNumberToCurrency(costeTotalConHipoteca),
          { highlight: true },
        )}
      </div>
    </div>
  );

  // El CTA (naranja, ancho completo) va debajo de los dos paneles.
  const resumenCta = isBotonContacto(CtaResumen)
    ? CtaResumen?.Texto && (
        <button
          type="button"
          onClick={() => abrirContacto(CtaResumen)}
          className="bg-ctacolor text-neutrals-0 rounded-button flex w-full items-center justify-center gap-2 bg-[var(--ctacolor)] px-6 py-4 text-[18px] transition-colors hover:bg-[rgb(var(--tw-color-button-bg-hover,var(--tw-color-secondary-500)))]"
        >
          {CtaResumen.Texto}
          <ChevronRight className="size-4 shrink-0" />
        </button>
      )
    : CtaResumen?.Texto &&
      CtaResumen?.Link && (
        <Link
          href={CtaResumen.Link}
          prefetch={false}
          className="bg-ctacolor text-neutrals-0 rounded-button flex w-full items-center justify-center gap-2 bg-[var(--ctacolor)] px-6 py-4 text-[18px] transition-colors hover:bg-[rgb(var(--tw-color-button-bg-hover,var(--tw-color-secondary-500)))]"
        >
          {CtaResumen.Texto}
          <ChevronRight className="size-4 shrink-0" />
        </Link>
      );

  const resumen = MostrarResumen ? (
    <div className="flex flex-1 flex-col gap-4">
      {resumenTopPanel}
      {resumenBottomPanel}
      {resumenCta}
    </div>
  ) : null;

  // Sin Resumen: layout histórico (header-left + form-right en una sola fila).
  // Con Resumen: header arriba, fila form + resumen abajo.
  const block = MostrarResumen ? (
    <div className="flex w-full flex-col gap-12">
      {header}
      {/* items-stretch: el card del formulario (izquierda) crece hasta igualar
          la altura de la columna del resumen (panel + desglose + CTA), como en
          Figma. El panel interno es flex-1, así que rellena la altura estirada. */}
      <div className="flex w-full flex-col gap-12 lg:flex-row lg:items-stretch lg:gap-10">
        {form}
        {resumen}
      </div>
    </div>
  ) : (
    <div className="flex w-full flex-col gap-12 lg:flex-row lg:items-center lg:gap-20">
      {header}
      {form}
    </div>
  );

  return noContainer ? (
    <>
      {block}
      {modal}
    </>
  ) : (
    <Container
      wrapper
      id={Ancla}
      {...sectionBgProps(FondoSeccion)}
      classNameWrapper={blockSpacing(Espaciado)}
    >
      {block}
      {modal}
    </Container>
  );
};

ComponentRevisadasCalculadoraHipoteca.displayName =
  'ComponentRevisadasCalculadoraHipoteca';
